/** Client-side sign-up field validation + normalization (Clerk remains the final authority). */

export type AuthFieldState = "neutral" | "valid" | "invalid"

export const PERSON_NAME_MAX_LENGTH = 50
export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 64
export const EMAIL_MAX_LENGTH = 254
export const PASSWORD_MAX_LENGTH = 128
export const VERIFICATION_CODE_MAX_LENGTH = 32

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const USERNAME_PATTERN = /^[a-zA-Z0-9_]+$/
/** Letters (any language) + combining marks, optional internal spaces / apostrophe / hyphen. */
const PERSON_NAME_PATTERN = /^[\p{L}\p{M}]+(?:[ '\-][\p{L}\p{M}]+)*$/u
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/g
const HAS_CONTROL_CHARS = /[\u0000-\u001F\u007F]/

export function stripControlChars(value: string): string {
  return value.replace(CONTROL_CHARS, "")
}

export function normalizePersonName(value: string): string {
  return stripControlChars(value).normalize("NFC").trim().replace(/\s+/g, " ")
}

export function normalizeUsername(value: string): string {
  return stripControlChars(value).trim()
}

export function normalizeEmail(value: string): string {
  return stripControlChars(value).trim().toLowerCase()
}

export function isValidPersonName(value: string): boolean {
  const normalized = normalizePersonName(value)
  return (
    normalized.length >= 1 &&
    normalized.length <= PERSON_NAME_MAX_LENGTH &&
    PERSON_NAME_PATTERN.test(normalized)
  )
}

/** @deprecated Prefer isValidPersonName — kept for call-site clarity during migration. */
export function isNonEmptyName(value: string): boolean {
  return isValidPersonName(value)
}

export function isValidUsername(value: string): boolean {
  const trimmed = normalizeUsername(value)
  return (
    trimmed.length >= USERNAME_MIN_LENGTH &&
    trimmed.length <= USERNAME_MAX_LENGTH &&
    USERNAME_PATTERN.test(trimmed)
  )
}

export function isValidEmail(value: string): boolean {
  const normalized = normalizeEmail(value)
  return (
    normalized.length > 0 &&
    normalized.length <= EMAIL_MAX_LENGTH &&
    EMAIL_PATTERN.test(normalized)
  )
}

export type PasswordRequirementFlags = {
  minLength: boolean
  hasLetter: boolean
  hasNumber: boolean
  hasSymbol: boolean
}

export function getPasswordRequirementFlags(password: string): PasswordRequirementFlags {
  return {
    minLength: password.length >= 8,
    hasLetter: /[A-Za-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSymbol: /[^A-Za-z0-9]/.test(password),
  }
}

export function isValidPassword(password: string): boolean {
  if (password.length > PASSWORD_MAX_LENGTH) return false
  if (HAS_CONTROL_CHARS.test(password)) return false
  const flags = getPasswordRequirementFlags(password)
  return flags.minLength && flags.hasLetter && flags.hasNumber && flags.hasSymbol
}

export function passwordsMatch(password: string, confirmPassword: string): boolean {
  return confirmPassword.length > 0 && password === confirmPassword
}

/** Live UI state: empty stays neutral; otherwise valid/invalid. */
export function fieldStateFromValue(
  value: string,
  isValid: (value: string) => boolean,
  touched = false
): AuthFieldState {
  if (!value && !touched) return "neutral"
  if (!value) return "invalid"
  return isValid(value) ? "valid" : "invalid"
}

export function passwordFieldState(password: string, touched = false): AuthFieldState {
  if (!password && !touched) return "neutral"
  if (!password) return "invalid"
  return isValidPassword(password) ? "valid" : "invalid"
}

/** Confirm turns green only when it matches a valid password. */
export function confirmPasswordFieldState(
  password: string,
  confirmPassword: string,
  touched = false
): AuthFieldState {
  if (!confirmPassword && !touched) return "neutral"
  if (!confirmPassword) return "invalid"
  return isValidPassword(password) && passwordsMatch(password, confirmPassword)
    ? "valid"
    : "invalid"
}

export type SignUpFieldValues = {
  firstName: string
  lastName: string
  username: string
  email: string
  password: string
  confirmPassword: string
  termsAccepted: boolean
}

export type NormalizedSignUpFields = {
  firstName: string
  lastName: string
  username: string
  email: string
  password: string
  confirmPassword: string
}

/** Strip control chars / normalize spacing before validation and Clerk submit. */
export function normalizeSignUpFields(values: {
  firstName: string
  lastName: string
  username: string
  email: string
  password: string
  confirmPassword: string
}): NormalizedSignUpFields {
  return {
    firstName: normalizePersonName(values.firstName),
    lastName: normalizePersonName(values.lastName),
    username: normalizeUsername(values.username),
    email: normalizeEmail(values.email),
    password: values.password,
    confirmPassword: values.confirmPassword,
  }
}

/** First blocking validation error for submit, or null when all required fields pass. */
export function getSignUpValidationError(values: SignUpFieldValues): string | null {
  const normalized = normalizeSignUpFields(values)

  if (!isValidPersonName(normalized.firstName)) {
    return "First name must use letters only (spaces, hyphens, and apostrophes allowed) and be at most 50 characters."
  }
  if (!isValidPersonName(normalized.lastName)) {
    return "Last name must use letters only (spaces, hyphens, and apostrophes allowed) and be at most 50 characters."
  }
  if (!isValidUsername(normalized.username)) {
    return "Username must be 3–64 characters and use only letters, numbers, or underscores."
  }
  if (!isValidEmail(normalized.email)) {
    return "Please enter a valid email address."
  }
  if (!isValidPassword(normalized.password)) {
    return "Password must be at least 8 characters and include a letter, a number, and a symbol."
  }
  if (!passwordsMatch(normalized.password, normalized.confirmPassword)) {
    return "Passwords do not match. Please try again."
  }
  if (!values.termsAccepted) {
    return "You must agree to the Terms of Service and Privacy Policy to create an account."
  }
  return null
}
